// /admin/integrations/api-keys — gestione API keys (Sprint 7).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiKeyListItem, ApiKeyScope } from '@audiq/shared';
import { API_KEY_SCOPES } from '@audiq/shared';
import { IconButton } from '@audiq/ui';
import { Ban } from 'lucide-react';
import { api, type ApiKeyCreatedResponse } from '../../lib/api';

export function ApiKeysPage() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin', 'api-keys'], queryFn: api.listApiKeys });
  const [showCreate, setShowCreate] = useState(false);
  const [createdKey, setCreatedKey] = useState<ApiKeyCreatedResponse | null>(null);

  const revokeMu = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] }),
  });

  if (list.isLoading)
    return (
      <p className="mono" style={{ color: 'var(--mute)' }}>
        Caricamento…
      </p>
    );
  if (list.error) return <p style={{ color: 'var(--danger)' }}>Errore: {String(list.error)}</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
          Chiavi API attive
        </h3>
        <button
          onClick={() => setShowCreate(true)}
          className="rounded-md px-4 py-2 text-sm font-medium"
          style={{ background: 'var(--primary)', color: '#fff' }}
        >
          + Nuova chiave
        </button>
      </div>

      <div
        className="overflow-x-auto rounded-md border"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--soft)' }}>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Nome
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Prefix
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Scopes
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Last used
              </th>
              <th className="text-start p-3 mono" style={{ color: 'var(--mute)' }}>
                Status
              </th>
              <th className="text-end p-3 mono" style={{ color: 'var(--mute)' }}>
                Azioni
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data?.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center" style={{ color: 'var(--mute)' }}>
                  Nessuna chiave creata. Click su "+ Nuova chiave" per iniziare.
                </td>
              </tr>
            )}
            {list.data?.map((k: ApiKeyListItem) => (
              <tr key={k.id} style={{ borderTop: '1px solid var(--line)' }}>
                <td className="p-3" style={{ color: 'var(--ink)' }}>
                  {k.name}
                </td>
                <td className="p-3 mono">audq_{k.key_prefix}…</td>
                <td className="p-3 text-xs">
                  {k.scopes.map((s) => (
                    <span
                      key={s}
                      className="me-1 rounded-md px-2 py-0.5 mono"
                      style={{ background: 'var(--soft)', color: 'var(--ink)' }}
                    >
                      {s}
                    </span>
                  ))}
                </td>
                <td className="p-3 text-xs" style={{ color: 'var(--mute)' }}>
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleString('it-IT') : '—'}
                </td>
                <td className="p-3">
                  {k.status === 'active' && (
                    <span className="text-xs" style={{ color: 'var(--primary-2)' }}>
                      ● attiva
                    </span>
                  )}
                  {k.status === 'revoked' && (
                    <span className="text-xs" style={{ color: 'var(--danger)' }}>
                      ● revocata
                    </span>
                  )}
                  {k.status === 'expired' && (
                    <span className="text-xs" style={{ color: 'var(--mute)' }}>
                      ● scaduta
                    </span>
                  )}
                </td>
                <td className="p-3 text-end">
                  {k.status === 'active' && (
                    <IconButton
                      icon={Ban}
                      label="Revoca"
                      tone="danger"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Revoca "${k.name}"? Le chiamate API smettono di funzionare immediatamente.`,
                          )
                        ) {
                          revokeMu.mutate(k.id);
                        }
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <CreateKeyDialog
          onClose={() => setShowCreate(false)}
          onCreated={(resp) => {
            setCreatedKey(resp);
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['admin', 'api-keys'] });
          }}
        />
      )}

      {createdKey && <CreatedKeyDialog created={createdKey} onClose={() => setCreatedKey(null)} />}
    </div>
  );
}

function CreateKeyDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (k: ApiKeyCreatedResponse) => void;
}) {
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['encounters:read']);
  const [rateLimit, setRateLimit] = useState(60);

  const createMu = useMutation({
    mutationFn: () =>
      api.createApiKey({
        name,
        scopes,
        rate_limit_per_minute: rateLimit,
      }),
    onSuccess: (resp) => onCreated(resp),
  });

  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="rounded-md p-6 w-full max-w-md"
        style={{ background: 'var(--paper)', border: '1px solid var(--line)' }}
      >
        <h3 className="serif text-2xl mb-4" style={{ color: 'var(--ink)' }}>
          Nuova chiave API
        </h3>

        <label className="block mb-3 text-sm" style={{ color: 'var(--ink)' }}>
          Nome (es. "Gestionale Studio Bianchi")
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <label className="block mb-3 text-sm" style={{ color: 'var(--ink)' }}>
          Rate limit (chiamate/min)
          <input
            type="number"
            min={1}
            max={10000}
            value={rateLimit}
            onChange={(e) => setRateLimit(Number(e.target.value))}
            className="mt-1 w-full rounded-md border px-3 py-2"
            style={{
              background: 'var(--surface)',
              borderColor: 'var(--line)',
              color: 'var(--ink)',
            }}
          />
        </label>

        <div className="mb-4 text-sm" style={{ color: 'var(--ink)' }}>
          <div className="mb-2 mono" style={{ color: 'var(--mute)' }}>
            Scopes:
          </div>
          {API_KEY_SCOPES.map((s) => (
            <label key={s} className="me-3 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={scopes.includes(s)}
                onChange={(e) => {
                  if (e.target.checked) setScopes([...scopes, s]);
                  else setScopes(scopes.filter((x) => x !== s));
                }}
              />
              <span className="mono text-xs">{s}</span>
            </label>
          ))}
        </div>

        {createMu.error && (
          <p className="text-sm mb-2" style={{ color: 'var(--danger)' }}>
            {String(createMu.error)}
          </p>
        )}

        <div className="flex gap-2 justify-end">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm"
            style={{ color: 'var(--mute)' }}
          >
            Annulla
          </button>
          <button
            disabled={!name.trim() || scopes.length === 0 || createMu.isPending}
            onClick={() => createMu.mutate()}
            className="rounded-md px-4 py-2 text-sm"
            style={{
              background: 'var(--primary)',
              color: '#fff',
              opacity: createMu.isPending ? 0.6 : 1,
            }}
          >
            {createMu.isPending ? 'Creazione…' : 'Crea chiave'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreatedKeyDialog({
  created,
  onClose,
}: {
  created: ApiKeyCreatedResponse;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      role="dialog"
      aria-modal
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        className="rounded-md p-6 w-full max-w-lg"
        style={{ background: 'var(--paper)', border: '1px solid var(--line)' }}
      >
        <h3 className="serif text-2xl mb-2" style={{ color: 'var(--ink)' }}>
          Chiave creata
        </h3>
        <p className="text-sm mb-4" style={{ color: 'var(--mute)' }}>
          <strong style={{ color: 'var(--danger)' }}>Salva questa chiave ora.</strong> Non sarà più
          mostrata dopo la chiusura di questa finestra.
        </p>
        <div
          className="rounded-md p-3 mono text-sm break-all"
          style={{ background: 'var(--soft)', color: 'var(--ink)' }}
        >
          {created.raw_key}
        </div>
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={async () => {
              await navigator.clipboard.writeText(created.raw_key);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="text-sm"
            style={{ color: 'var(--primary-2)' }}
          >
            {copied ? '✓ Copiato' : 'Copia negli appunti'}
          </button>
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm"
            style={{ background: 'var(--primary)', color: '#fff' }}
          >
            Ho salvato la chiave
          </button>
        </div>
      </div>
    </div>
  );
}
